import {useEffect} from 'react'
import {NavLink, Outlet, useLocation} from 'react-router-dom'
import {
  AlertTriangle,
  Boxes,
  CheckCircle2,
  Gauge,
  KanbanSquare,
  ListChecks,
  Sparkles,
  Timer,
  UserRound,
  UsersRound,
} from 'lucide-react'
import {useApp} from '../../context/AppContext'
import type {Page} from '../../types'
import {GetPermissions} from '../../../../modules/auth'
import './devops.css'

const navigation: Array<{
  page: Page
  label: string
  path: string
  permission: string
  icon: React.ElementType
}> = [
  {
    page: 'dashboard',
    label: 'Visão geral',
    path: '/devops/dashboard',
    permission: 'devops-dashboard',
    icon: Gauge,
  },
  {
    page: 'boards',
    label: 'Quadros',
    path: '/devops/quadros',
    permission: 'devops-tasks',
    icon: KanbanSquare,
  },
  {
    page: 'tasks',
    label: 'Tarefas',
    path: '/devops/tarefas',
    permission: 'devops-tasks',
    icon: ListChecks,
  },
  {
    page: 'sprints',
    label: 'Sprints',
    path: '/devops/sprints',
    permission: 'devops-sprints',
    icon: Timer,
  },
  {
    page: 'teams',
    label: 'Equipes',
    path: '/devops/equipes',
    permission: 'devops-teams',
    icon: UsersRound,
  },
  {
    page: 'users',
    label: 'Pessoas',
    path: '/devops/usuarios',
    permission: 'devops-users',
    icon: UserRound,
  },
]

export default function DevOpsSectionLayout() {
  const {state, dispatch} = useApp()
  const location = useLocation()
  const permissions = GetPermissions()
  const visibleNavigation = navigation.filter((item) =>
    permissions?.some((feature) => feature.name.includes(item.permission))
  )

  useEffect(() => {
    const activeItem = navigation.find((item) => location.pathname.startsWith(item.path))
    if (activeItem && activeItem.page !== state.currentPage) {
      dispatch({type: 'SET_PAGE', payload: activeItem.page})
    }
  }, [dispatch, location.pathname, state.currentPage])

  const activeSprint = state.sprints.find((sprint) => sprint.status === 'active')
  const inProgress = state.tasks.filter((task) => task.status === 'in_progress').length
  const completed = state.tasks.filter((task) => task.status === 'done').length
  const online = state.users.filter((user) => user.isOnline).length

  return (
    <section className='devops-app'>
      <div className='devops-workspace-header'>
        <div className='devops-workspace-header__glow' aria-hidden='true' />
        <div className='devops-workspace-header__inner'>
          <div className='devops-workspace-summary'>
            <div className='devops-workspace-brand'>
              <span className='devops-workspace-brand__icon'>
                <Boxes size={22} strokeWidth={1.8} />
              </span>
              <div>
                <div className='devops-eyebrow'>
                  <Sparkles size={12} />
                  Workspace de entregas
                </div>
                <h1>Central DevOps</h1>
                <p>Planeje, acompanhe e entregue com clareza em um só lugar.</p>
              </div>
            </div>

            <div className='devops-workspace-metrics' aria-label='Resumo do workspace'>
              <div className='devops-workspace-metric'>
                <Timer size={15} />
                <span>
                  <small>Sprint atual</small>
                  <strong>{activeSprint?.name || 'Planejamento'}</strong>
                </span>
              </div>
              <div className='devops-workspace-metric'>
                <ListChecks size={15} />
                <span>
                  <small>Em andamento</small>
                  <strong>{inProgress} tarefas</strong>
                </span>
              </div>
              <div className='devops-workspace-metric'>
                <CheckCircle2 size={15} />
                <span>
                  <small>Concluídas</small>
                  <strong>{completed} tarefas</strong>
                </span>
              </div>
              <div className='devops-workspace-metric'>
                <UsersRound size={15} />
                <span>
                  <small>Time online</small>
                  <strong>{online} pessoas</strong>
                </span>
              </div>
            </div>
          </div>

          <nav className='devops-section-nav' aria-label='Seções de DevOps'>
            {visibleNavigation.map(({page, label, path, icon: Icon}) => (
              <NavLink
                key={page}
                to={path}
                className={({isActive}) =>
                  `devops-section-nav__item${isActive ? ' is-active' : ''}`
                }
              >
                <Icon size={16} strokeWidth={2} />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      <div className='devops-route-content'>
        {state.error ? (
          <div className='devops-page'>
            <div className='devops-empty-state'>
              <div>
                <AlertTriangle size={28} className='mx-auto' />
                <h2 className='text-base font-semibold text-slate-700'>
                  Não foi possível carregar o workspace
                </h2>
                <p className='mt-1 text-sm text-slate-500'>{state.error}</p>
              </div>
            </div>
          </div>
        ) : (
          <Outlet />
        )}
      </div>
    </section>
  )
}
